import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

/**
 * Landing page for the on-prem portal SSO flow.
 *
 * Flow:
 *   1. CCPPortal computes an HMAC and redirects the browser to:
 *        GET https://<api>/api/auth/sso?token=...&email=...&ts=...
 *   2. The .NET API validates HMAC + timestamp, mints a JWT, and 302s
 *      the browser here:
 *        https://<spa>/sso-callback?token=<jwt>&refresh=<refresh>
 *   3. This component pulls the tokens out of the query string, hands
 *      them to AuthContext (which seeds the cookies and fetches /me),
 *      then navigates to /dashboard.
 *
 * The URL is sanitized via history.replaceState immediately so the JWT
 * doesn't sit in the address bar / browser history.
 */
const SsoCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { establishSsoSession } = useAuth();
  const [error, setError] = useState<string | null>(null);
  // StrictMode in dev double-invokes effects. Without this guard the
  // session would be established twice and the navigate would race itself.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const token = searchParams.get('token');
    const refreshToken = searchParams.get('refresh');

    // Strip the tokens from the URL before doing anything else so they
    // don't linger in browser history or get logged by the SPA's analytics.
    window.history.replaceState(null, '', '/sso-callback');

    if (!token || !refreshToken) {
      setError('Missing SSO tokens. Please return to the portal and try again.');
      return;
    }

    establishSsoSession(token, refreshToken)
      .then(() => navigate('/dashboard', { replace: true }))
      .catch(() => {
        setError('SSO sign-in failed. Please return to the portal and try again.');
      });
  }, [searchParams, establishSsoSession, navigate]);

  if (error) {
    return (
      <div className="report-view">
        <div className="report-content">
          <div className="error-container">
            <div className="error-message">
              <h3>SSO Error</h3>
              <p>{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="report-view">
      <div className="report-content">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Signing you in…</p>
        </div>
      </div>
    </div>
  );
};

export default SsoCallback;
